import { useNavigate } from '@tanstack/react-router';
import { Button, H2, Paragraph, Separator, Spinner, Switch, Text, XStack, YStack } from 'tamagui';
import type { MailAccount } from '@mrrmlab/api-client';
import { useDeleteMailAccount, useMailAccounts, useUpdateMailAccount } from '../mail/hooks';
import { SenderMemorySection } from '../components/sender-memory-section';

export function EinstellungenMailRoute() {
  const navigate = useNavigate();
  const { data: accounts, isLoading } = useMailAccounts();
  const { mutate: deleteAccount } = useDeleteMailAccount();
  const { mutate: updateAccount } = useUpdateMailAccount();

  return (
    <YStack padding="$4" gap="$5" maxWidth={640}>
      <YStack gap="$3">
        <XStack justifyContent="space-between" alignItems="center">
          <H2>Mailkonten</H2>
          <Button size="$3" onPress={() => navigate({ to: '/mail/new-account' })}>
            + Konto
          </Button>
        </XStack>

        {isLoading ? (
          <Spinner />
        ) : !accounts?.length ? (
          <Paragraph color="$color10">Noch kein Konto eingerichtet.</Paragraph>
        ) : (
          <YStack gap="$2">
            {accounts.map(account => (
              <MailAccountRow
                key={account.id}
                account={account}
                onDelete={() => deleteAccount(account.id)}
                onToggleTrackingScan={(enabled) =>
                  updateAccount({ id: account.id, dto: { trackingScanEnabled: enabled } })
                }
              />
            ))}
          </YStack>
        )}
      </YStack>

      <Separator />

      <SenderMemorySection />
    </YStack>
  );
}

function MailAccountRow({
  account,
  onDelete,
  onToggleTrackingScan,
}: {
  account: MailAccount;
  onDelete: () => void;
  onToggleTrackingScan: (enabled: boolean) => void;
}) {
  return (
    <YStack
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      gap="$2"
    >
      <XStack alignItems="center" gap="$3">
        <YStack flex={1}>
          <Text fontSize="$5" fontWeight="600">{account.label}</Text>
          <Text fontSize="$3" color="$color10">{account.username} · {account.host}</Text>
        </YStack>
        <Button size="$2" variant="outlined" color="$red10" onPress={onDelete}>
          Entfernen
        </Button>
      </XStack>
      <XStack alignItems="center" gap="$3" paddingTop="$2">
        <YStack flex={1}>
          <Text fontSize="$3">Pakete-Scan</Text>
          <Text fontSize="$2" color="$color10">
            Eingehende Mails auf Tracking-Nummern prüfen
          </Text>
        </YStack>
        <Text
          fontSize="$2"
          fontWeight="600"
          color={account.trackingScanEnabled ? '$green10' : '$color10'}
          minWidth={28}
          textAlign="right"
        >
          {account.trackingScanEnabled ? 'An' : 'Aus'}
        </Text>
        <Switch
          size="$3"
          checked={account.trackingScanEnabled}
          onCheckedChange={onToggleTrackingScan}
          backgroundColor={account.trackingScanEnabled ? '$green9' : '$color6'}
          borderWidth={1}
          borderColor={account.trackingScanEnabled ? '$green10' : '$color8'}
        >
          <Switch.Thumb animation="quicker" backgroundColor="white" />
        </Switch>
      </XStack>
    </YStack>
  );
}
